import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  ButtonGroup,
  Stack,
  TextField,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import MuiToggleButton from "@mui/material/ToggleButton";
import { styled } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useState } from "react";
import { getConfirmation, setConfirmation } from "../../api/confirmation";
import { addSchedule, getSchedules, setSchedule } from "../../api/schedules";
import { SCHEDULE_TYPE } from "../../const/enums";

const ToggleButton = styled(MuiToggleButton)({
  "&.Mui-selected, &.Mui-selected:hover": {
    color: "white",
    backgroundColor: "#21456A",
  },
});

const OK = 1;
const NEGATIVE_OK = 2;
const NG = 3;

export default function Calendar() {
  const [pivot, setPivot] = useState(moment().startOf("month"));
  const [choice, setChoice] = useState({}); // { date: [id, status, on_duty_comment] } の型

  const { data, refetch } = useQuery({
    queryKey: ["schedule", pivot],
    queryFn: () =>
      getSchedules(
        pivot.year(),
        pivot.month() + 1,
        SCHEDULE_TYPE.ON_DUTY_REQUEST,
      ),
    throwOnError: true,
  });

  const { data: confirmation, refetch: refetchConfirmation } = useQuery({
    queryKey: ["confirmation", pivot],
    queryFn: () => getConfirmation(pivot.year(), pivot.month() + 1),
    throwOnError: true,
  });

  useEffect(() => {
    if (!data) return;

    const nextChoice = data.reduce((acc, schedule) => {
      const { date, id, is_negative, is_negative_assignable, on_duty_comment } =
        schedule;
      let status;

      if (is_negative) {
        status = NG;
      } else if (is_negative_assignable) {
        status = NEGATIVE_OK;
      } else {
        status = OK;
      }

      acc[date] = [id, status, on_duty_comment];
      return acc;
    }, {});

    setChoice(nextChoice);
  }, [data]);

  const handleChoice = async (date, selectedRequest, onDutyComment) => {
    /** memo
     * is_negative=false & is_negative_assignable=false: OK ==> まる
     * is_negative=false & is_negative_assignable=true: NEGATIVE_OKj ==> サンカク
     * is_negative=true & is_negative_assignable=false: NG ==> バツ
     */
    let id = choice[date]?.[0];

    // まるばつさんかくでの当直希望更新処理
    if (selectedRequest) {
      if (id) {
        // update
        await setSchedule({
          id: id,
          date: date,
          schedule_type_id: SCHEDULE_TYPE.ON_DUTY_REQUEST,
          is_negative: selectedRequest === NG,
          is_negative_assignable: selectedRequest === NEGATIVE_OK,
        });
      } else {
        // create
        const schedule = await addSchedule({
          date: date,
          schedule_type_id: SCHEDULE_TYPE.ON_DUTY_REQUEST,
          is_negative: selectedRequest === NG,
          is_negative_assignable: selectedRequest === NEGATIVE_OK,
        });
        id = schedule.id;
      }
      const nextChoice = { ...choice };
      const onDutyComment = choice[date]?.[2];
      nextChoice[date] = [id, selectedRequest, onDutyComment];
      setChoice(nextChoice);
    }

    // コメント更新処理
    if (onDutyComment) {
      if (id) {
        // update
        await setSchedule({
          id: id,
          date: date,
          schedule_type_id: SCHEDULE_TYPE.ON_DUTY_REQUEST,
          on_duty_comment: onDutyComment,
        });
      } else {
        // create
        const schedule = await addSchedule({
          date: date,
          schedule_type_id: SCHEDULE_TYPE.ON_DUTY_REQUEST,
          on_duty_comment: onDutyComment,
        });
        id = schedule.id;
      }
      const nextChoice = { ...choice };
      const selectedRequest = choice[date]?.[1];
      nextChoice[date] = [id, selectedRequest, onDutyComment];
      setChoice(nextChoice);
    }
  };

  const handleShowConfirm = () => {
    if (!confirmation) {
      return;
    }
    if (!confirmation.confirmed) {
      // eslint-disable-next-line no-restricted-globals
      if (confirm("この月の予定を確定しますか？")) {
        setConfirmation({
          year: pivot.year(),
          month: pivot.month() + 1,
          confirmed: true,
        }).then(async () => {
          await refetchConfirmation();
          alert("確定しました");
        });
      }
    } else {
      // eslint-disable-next-line no-restricted-globals
      if (confirm("この月の予定を確定解除しますか？")) {
        setConfirmation({
          year: pivot.year(),
          month: pivot.month() + 1,
          confirmed: false,
        }).then(async () => {
          await refetchConfirmation();
          alert("確定解除しました");
        });
      }
    }
  };

  const handleThisMonth = async () => {
    setPivot(moment().startOf("month").clone());
    await refetch();
  };

  const handleNextMonth = async () => {
    setPivot(pivot.startOf("month").add("month", 1).clone());
    await refetch();
  };

  const handlePrevMonth = async () => {
    setPivot(pivot.startOf("month").add("month", -1).clone());
    await refetch();
  };

  const now = pivot.clone();
  const today = moment().format("YYYY-MM-DD");
  const start = now.startOf("month"); // 月初めの日付
  const end = moment(now).endOf("month"); // 月終わりの日付
  const calendar = [];

  const cellStyles = {
    borderLeft: 1,
    borderTop: 1,
    borderColor: "#999",
    width: "100%",
    paddingTop: "4px",
    paddingBottom: "4px",
  };

  // これ何使ってるか不明
  // [...Array(Number(start.format("d")))].map((_, i) =>
  //   // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
  //   calendar.push(<Box sx={{ ...cellStyles }} key={i} />)
  // );

  while (start.unix() <= end.unix()) {
    const date = start.format("YYYY-MM-DD");
    calendar.push(
      <Box
        sx={{
          ...cellStyles,
          paddingLeft: "5px",
          paddingRight: "4px",
          overflow: "hidden",
          background: date === today ? "#ffefc9" : "#fff",
        }}
        key={date}
      >
        <Typography>{start.format("D")}</Typography>
        <Box display="flex" justifyContent="center">
          <ToggleButtonGroup
            fullWidth
            key={date}
            sx={{
              padding: "8px 14px",
            }}
            value={choice[date]?.[1]}
            disabled={confirmation?.confirmed}
            aria-label="on duty requirement"
          >
            <ToggleButton
              key={`${date}.1`}
              value={NEGATIVE_OK}
              aria-label="Negative OK"
              onClick={(e) => {
                e.preventDefault();
                const current = choice[date]?.[1];
                handleChoice(
                  date,
                  current === NEGATIVE_OK ? OK : NEGATIVE_OK,
                  undefined,
                );
              }}
            >
              <ChangeHistoryIcon />
            </ToggleButton>
            <ToggleButton
              key={`${date}.2`}
              value={NG}
              aria-label="NG"
              onClick={(e) => {
                e.preventDefault();
                const current = choice[date]?.[1];
                handleChoice(date, current === NG ? OK : NG, undefined);
              }}
            >
              <CloseIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <TextField
          id="outlined-basic"
          value={choice[date]?.[2]}
          placeholder="Enterで保存"
          onChange={(e) => {
            e.preventDefault();
            const id = choice[date]?.[0];
            const selectedRequest = choice[date]?.[1];
            const nextChoice = { ...choice };
            nextChoice[date] = [id, selectedRequest, e.target.value];
            setChoice(nextChoice);
          }}
          onKeyDown={(e) => {
            if (e.code === "Enter") {
              handleChoice(date, undefined, e.target.value);
              e.target.blur();
            }
          }}
          variant="outlined"
          size="small"
          sx={{
            marginTop: "5px",
            paddingX: "5px",
            paddingY: "5px",
            "& input::placeholder": {
              fontSize: "0.9rem",
            },
          }}
        />
      </Box>,
    );
    start.add(1, "days");
  }

  // 枠線描画?
  [...Array((7 - (calendar.length % 7)) % 7)].map((_, i) =>
    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
    calendar.push(<Box sx={{ ...cellStyles }} key={i + calendar.length} />),
  );

  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: "40px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          alignSelf: "stretch",
          marginBottom: "24px",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: "Noto Sans JP",
            fontStyle: "normal",
            fontWeight: "400",
            fontSize: "24px",
            lineHeight: "133.4%",
            color: "rgba(0, 0, 0, 0.6)",
            marginLeft: "12px",
          }}
        >
          {pivot.format("YYYY年M月")}
        </Typography>
        <Stack
          direction="row"
          sx={{
            gap: "8px",
            width: "250px",
            height: "40px",
          }}
        >
          <Button
            variant="contained"
            color={confirmation?.confirmed ? "primary" : "error"}
            size="medium"
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "8px 16px",
              width: "100px",
              height: "40px",
              /* elevation/2 */
              boxShadow:
                "0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
              borderRadius: "6px",
            }}
            onClick={handleShowConfirm}
          >
            <Typography
              sx={{
                color: "var(--primary-contrast, #FFF)",
                /* button/medium */
                fontFamily: "Noto Sans JP",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "500",
                lineHeight: "24px" /* 171.429% */,
                letterSpacing: "1.25px",
                textTransform: "uppercase",
              }}
            >
              {confirmation?.confirmed ? "確定済" : "未確定"}
            </Typography>
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "8px 16px",
              width: "62px",
              height: "40px",
              background: "#21456A",
              /* elevation/2 */
              boxShadow:
                "0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
              borderRadius: "6px",
            }}
            onClick={handleThisMonth}
          >
            <Typography
              sx={{
                color: "var(--primary-contrast, #FFF)",
                /* button/medium */
                fontFamily: "Noto Sans JP",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "500",
                lineHeight: "24px" /* 171.429% */,
                letterSpacing: "1.25px",
                textTransform: "uppercase",
              }}
            >
              今日
            </Typography>
          </Button>
          <ButtonGroup
            color="primary"
            orientation="horizontal"
            variant="contained"
            sx={{
              borderRadius: "var(--borderRadius, 6px)",
              background: "var(--primary-main, #21456A)",
              /* elevation/2 */
              boxShadow:
                "0px 3px 1px -2px rgba(0, 0, 0, 0.20), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
            }}
          >
            <Button
              sx={{
                padding: "8px",
                width: "40px",
                height: "40px",
                borderRadius: "4px",
              }}
              onClick={handlePrevMonth}
            >
              <svg
                role="img"
                aria-label="Previous month"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M14.71 6.71047C14.32 6.32047 13.69 6.32047 13.3 6.71047L8.70998 11.3005C8.31998 11.6905 8.31998 12.3205 8.70998 12.7105L13.3 17.3005C13.69 17.6905 14.32 17.6905 14.71 17.3005C15.1 16.9105 15.1 16.2805 14.71 15.8905L10.83 12.0005L14.71 8.12047C15.1 7.73047 15.09 7.09047 14.71 6.71047Z"
                  fill="white"
                />
              </svg>
            </Button>
            <Button
              sx={{
                padding: "8px",
                width: "40px",
                height: "40px",
                borderRadius: "4px",
              }}
              onClick={handleNextMonth}
            >
              <svg
                role="img"
                aria-label="Next month"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9.29006 6.70978C8.90006 7.09978 8.90006 7.72978 9.29006 8.11978L13.1701 11.9998L9.29006 15.8798C8.90006 16.2698 8.90006 16.8998 9.29006 17.2898C9.68006 17.6798 10.3101 17.6798 10.7001 17.2898L15.2901 12.6998C15.6801 12.3098 15.6801 11.6798 15.2901 11.2898L10.7001 6.69978C10.3201 6.31978 9.68006 6.31978 9.29006 6.70978Z"
                  fill="white"
                />
              </svg>
            </Button>
          </ButtonGroup>
        </Stack>
      </Box>
      <Box>
        <Typography fontSize={14} style={{ marginLeft: "4px" }}>
          ・未選択の場合は「○：当直またはオンコールとして割当てて良い」として扱われます。
        </Typography>
        <Typography
          fontSize={14}
          style={{ marginLeft: "4px", fontWeight: "bold", color: "#FF0000" }}
        >
          ・毎月10日までに入力の完了をお願いします。
        </Typography>
      </Box>
      <Box
        sx={{
          marginTop: "10px",
          width: "100%",
          display: "grid",
          gridAutoFlow: "row",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0,
          borderRight: 1,
          borderBottom: 1,
          borderColor: "#999",
        }}
      >
        <Box sx={{ ...cellStyles, textAlign: "center", color: "#f00" }}>日</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>月</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>火</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>水</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>木</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>金</Box>
        <Box sx={{ ...cellStyles, textAlign: "center", color: "#00f" }}>土</Box>
        {calendar}
      </Box>
    </>
  );
}
